import type { AdditionalQuestionsProps } from '../AdditionalQuestions/types';
import type { ExitStrategyProps } from '../ExitStrategy/types';
import type { PiiConfirmationProps } from '../PiiConfirmation/types';
import { SubmissionStatus } from '../ResultsPage/types';
import {
  SingleSubmissionStep,
  type SingleProgramSubmissionProps,
} from './types';
import { type FC } from 'react';
import { resolveApplicationCtaHref } from '../../utils/resolveApplicationCtaHref';

type Props = {
  PiiConfirmationComponent: FC<PiiConfirmationProps>;
  ExitStrategyComponent: FC<ExitStrategyProps>;
  AdditionalQuestionsComponent: FC<AdditionalQuestionsProps>;
} & SingleProgramSubmissionProps;

const SingleProgramSubmission: FC<Props> = ({
  PiiConfirmationComponent,
  ExitStrategyComponent,
  AdditionalQuestionsComponent,
  SubmissionStatusComponent,
  step,
  selectedResult,
  startTime,
  piiLeadErrors,
  editMode,
  setEditMode,
  degree,
  isLoading,
  leadSubmissionStatus,
  setLeadSubmissionStatus,
  canSubmit,
  formHeading,
  formSubHeading,
  firstName,
  lastName,
  email,
  phone,
  setFirstName,
  setLastName,
  setEmail,
  setPhone,
  formErrors,
  questions,
  onProgramSubmit,
  onNextSteps: goToSlideoutExitStrategy,
  onBackButtonClick,
  onCloseExperience,
  productClickedCorrelationId,
  dslConfigId,
  studentLoansInlineContent,
  extraCtaConfig,
  partnerIdentifiers,
  showExitStrategyCloseButton,
}) => {
  // Unqualified responses to the additional-questions form are rendered inline
  // inside the AdditionalQuestions step so the user keeps the school/program
  // context. Every other terminal status (and unqualified responses from other
  // steps) still falls through to the full-screen SubmissionStatusComponent.
  const isUnqualifiedOnAdditionalQuestions =
    leadSubmissionStatus === SubmissionStatus.UNQUALIFIED &&
    step === SingleSubmissionStep.ADDITIONAL_QUESTIONS;

  const isStatusRenderable =
    !isUnqualifiedOnAdditionalQuestions &&
    (leadSubmissionStatus === SubmissionStatus.SUBMITTING ||
      leadSubmissionStatus === SubmissionStatus.ERROR ||
      leadSubmissionStatus === SubmissionStatus.SUBMITTED ||
      leadSubmissionStatus === SubmissionStatus.UNQUALIFIED);

  if (isStatusRenderable && SubmissionStatusComponent) {
    return (
      <SubmissionStatusComponent
        status={leadSubmissionStatus}
        schoolName={selectedResult?.schoolName}
        onRetry={onProgramSubmit}
        onCloseExperience={onCloseExperience}
      />
    );
  }

  const stepMap: Record<
    SingleSubmissionStep,
    | typeof PiiConfirmationComponent
    | typeof ExitStrategyComponent
    | typeof AdditionalQuestionsComponent
  > = {
    PII_CONFIRMATION: PiiConfirmationComponent,
    EXIT_STRATEGY: ExitStrategyComponent,
    ADDITIONAL_QUESTIONS: AdditionalQuestionsComponent,
  };

  const StepComponent = stepMap[step as keyof typeof stepMap];

  if (!StepComponent) return null;

  const leadForCta = {
    firstName,
    lastName,
    email,
    phoneNumber: phone,
  };

  const stepProps: Record<string, any> = {
    selectedResult,
    startTime,
    piiLeadErrors,
    editMode,
    setEditMode,
    degree,
    isLoading,
    leadSubmissionStatus,
    setLeadSubmissionStatus,
    canSubmit,
    formHeading,
    formSubHeading,
    firstName,
    lastName,
    email,
    phone,
    setFirstName,
    setLastName,
    setEmail,
    setPhone,
    formErrors,
    questions,
    onFormError: () => {},
    onProgramSubmit,
    formCorrelationId: '',
    setPiiLeadErrors: () => {},
    setFormErrors: () => {},
    tcpa: selectedResult?.tcpa || '',
    schoolName: selectedResult?.schoolName || '',
    schoolSlug: selectedResult?.schoolSlug || '',
    city: selectedResult?.city,
    state: selectedResult?.state,
    programName: selectedResult?.programName || '',
    programFlow: selectedResult?.programFlow || [],
    tcpaSecondParagraph: selectedResult?.tcpaSecondParagraph || '',
    imageURL: selectedResult?.imageURL,
    valueProps: selectedResult?.valueProps,
    programDates: selectedResult?.programDates,
    onBackButtonClick,
    onCloseExperience,
    introText:
      selectedResult?.confirmationShortIntroText ||
      selectedResult?.confirmationText ||
      '',
    applicationCtaUrl: resolveApplicationCtaHref(selectedResult, {
      lead: leadForCta,
      partnerIdentifiers,
    }),
    applicationCtaLabel: selectedResult?.applicationCtaLabel,
    applicationCtaFieldMap: selectedResult?.applicationCtaFieldMap,
    productClickedCorrelationId,
    dslConfigId,
    studentLoansInlineContent,
    extraCta: extraCtaConfig
      ? {
          enabled: extraCtaConfig.enabled,
          label: extraCtaConfig.label,
          eventName: extraCtaConfig.eventName,
          destination: selectedResult?.postLeadCta,
        }
      : undefined,
    partnerIdentifiers,
    ...((extraCtaConfig && selectedResult?.postLeadCta?.url) ||
    selectedResult?.applicationCtaFieldMap
      ? { lead: leadForCta }
      : {}),
    showCloseButton: showExitStrategyCloseButton,
  };

  const Component = StepComponent as FC<Record<string, any>>;
  return <Component {...stepProps} />;
};

export default SingleProgramSubmission;
